import { useState, useEffect } from "react";
import { Check, Clock } from "lucide-react";
interface PipelineStep {
  title: string;
  items: {
    name: string;
    duration?: string;
    status: "complete" | "pending" | "waiting";
  }[];
}
const DataPipelineAnimation = () => {
  const [visibleSteps, setVisibleSteps] = useState<number>(0);
  const [animatingItems, setAnimatingItems] = useState<{
    [key: string]: number;
  }>({});
  const pipelineSteps: PipelineStep[] = [{
    title: "Connect",
    items: [{
      name: "ERP System",
      duration: "litre",
      status: "complete"
    }]
  }, {
    title: "Get",
    items: [{
      name: "Generator",
      duration: "litre",
      status: "complete"
    }, {
      name: "Raw material",
      duration: "kg",
      status: "complete"
    }, {
      name: "Business travel",
      duration: "km",
      status: "complete"
    }]
  }, {
    title: "Update to Carbonwize",
    items: [{
      name: "Scope 1 Stationary",
      duration: "50 kgCO2e",
      status: "pending"
    }, {
      name: "Scope 3 Category 1",
      duration: "30 kgCO2e",
      status: "pending"
    }, {
      name: "Scope 3 Category 6",
      duration: "44 kgCO2e",
      status: "pending"
    }]
  }];
  useEffect(() => {
    const runAnimation = () => {
      // Reset animation
      setVisibleSteps(0);
      setAnimatingItems({});

      // Animate steps appearing one by one
      pipelineSteps.forEach((_, stepIndex) => {
        setTimeout(() => {
          setVisibleSteps(prev => Math.max(prev, stepIndex + 1));

          // Animate items within each step
          pipelineSteps[stepIndex].items.forEach((__, itemIndex) => {
            setTimeout(() => {
              setAnimatingItems(prev => ({
                ...prev,
                [`${stepIndex}-${itemIndex}`]: 1
              }));
            }, itemIndex * 150);
          });
        }, stepIndex * 600);
      });
    };

    // Run animation initially
    runAnimation();

    // Loop animation every 5 seconds
    const loopInterval = setInterval(() => {
      runAnimation();
    }, 5000);
    return () => {
      clearInterval(loopInterval);
    };
  }, []);
  const getStatusIcon = (status: string) => {
    if (status === "complete") {
      return <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-accent flex items-center justify-center flex-shrink-0">
          <Check className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-dark" />
        </div>;
    }
    return <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full border-2 border-amber-500 flex items-center justify-center flex-shrink-0">
        <Clock className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-amber-500" />
      </div>;
  };
  return <div className="w-full h-full bg-transparent rounded-xl p-3 sm:p-6 flex flex-col overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <h3 className="text-primary-foreground font-mono text-xs sm:text-sm font-medium">carbon-data-pipeline.yml</h3>
        <p className="text-muted-foreground text-[10px] sm:text-xs font-mono">on: data_import</p>
      </div>

      {/* Pipeline Steps */}
      <div className="flex-1 flex items-center justify-start sm:justify-center gap-2 relative min-w-[480px] sm:min-w-0">
        {pipelineSteps.map((step, stepIndex) => <div key={step.title} className={`flex items-center transition-all duration-500 ${stepIndex < visibleSteps ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            {/* Step Card */}
            <div className="bg-background/75 border border-[#2ec27c] rounded-lg p-2 sm:p-3 min-w-[120px] sm:min-w-[140px]">
              <div className="text-dark text-[10px] sm:text-xs font-bold mb-2 px-1">{step.title}</div>
              <div className="space-y-1 sm:space-y-2">
                {step.items.map((item, itemIndex) => <div key={item.name} className={`flex items-center gap-1.5 sm:gap-2 p-1.5 sm:p-2 rounded-md bg-background/90 border border-border transition-all duration-300 ${animatingItems[`${stepIndex}-${itemIndex}`] ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'}`}>
                    {getStatusIcon(item.status)}
                    <div className="flex-1 min-w-0">
                      <p className="text-foreground text-[10px] sm:text-xs font-medium truncate">{item.name}</p>
                      {item.duration ? <p className="text-muted-foreground text-[8px] sm:text-[10px]">{item.duration}</p> : <p className="text-amber-500 text-[8px] sm:text-[10px]">Waiting for approval</p>}
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Connector Line */}
            {stepIndex < pipelineSteps.length - 1 && <div className={`flex items-center mx-1 transition-all duration-300 delay-300 ${stepIndex + 1 < visibleSteps ? 'opacity-100' : 'opacity-0'}`}>
                <div className="w-4 sm:w-6 border-t-2 border-dashed border-[#2ec27c]" />
              </div>}
          </div>)}
      </div>
    </div>;
};
export default DataPipelineAnimation;